// Home Page Content Data
// Single source of truth for human view, robot view, and markdown generation
// Home page with AI Visibility positioning



// Type definitions
export interface Problem {
  title: string
  description: string
}

export interface Solution {
  title: string
  headline: string
  description: string
}

export interface Feature {
  title: string
  description: string
  iconName: string
}

export interface BuiltForItem {
  title: string
  description: string
  iconName: string
}

export interface FAQ {
  question: string
  answer: string
}

export interface PricingTier {
  title: string
  subtitle: string
  price: string
  pricePeriod?: string
  priceNote: string
  features: string[]
  ctaText: string
  ctaUrl: string
  isPopular: boolean
  popularText?: string
}

export interface CommercialPathStep {
  step: string
  title: string
  description: string
}

export interface Stat {
  value: string
  label: string
}

export interface EvidenceStat {
  value: string
  headline: string
  description: string
  source: string
}

export interface Testimonial {
  /** The reviewer's own title for their review. */
  head: string
  said: string
  who: string
}

export interface Advantage {
  /**
   * Optional. The design system has no eyebrows: a section begins with its
   * headline. Kept on the type because other surfaces still read it.
   */
  eyebrow?: string
  headline: string
  description: string
}

// Page metadata
export const metadata = {
  slug: 'home',
  title: 'SuperSchema | Make your HubSpot website ready for AI',
  description:
    'Make it easier for AI to understand what you sell, who you serve, and what makes your business worth considering. SuperSchema turns your HubSpot CMS pages into structured, AI-readable information, and verifies that it is live.',
  canonicalUrl: 'https://superschema.ai',
  datePublished: '2025-01-01',
  dateModified: new Date().toISOString().split('T')[0],
}

// Hero content
export const heroContent = {
  /*
    The eyebrow says who the page is for, and the headline now says it
    again: this is a HubSpot product and the page stops pretending
    otherwise (Kevin, 2026-09-15). The supporting line is the offer in one
    breath: what AI should understand, what the software does to the pages,
    and that it checks the result is live.
  */
  eyebrow: 'For websites built on HubSpot',
  headline: 'Make your HubSpot website ready for AI.',
  subheadline:
    'Make it easier for AI to understand what you sell, who you serve, and what makes your business worth considering. SuperSchema turns your HubSpot CMS pages into structured, AI-readable information, and verifies that it is live.',
  ctaText: 'Start free',
  ctaUrl: '/sign-up',
  /*
    Under the scan box, where a person is about to type. The eyebrow has
    already said who the page is for, but a reader who skipped it and went
    straight for the box needs to hear it again right there: a scan of a
    site that is not on HubSpot cannot be fixed in one click, and every
    such scan lands in the onboarding funnel as a drop that was never ours
    to win (Kevin, 2026-09-15).
  */
  scanNote: 'This scan is for websites built on HubSpot. Enter your HubSpot site’s address above.',
  /*
    Under the Start free button, which is what the page draws now: sign-up
    is the first step, and this one line says what the account is. Short
    facts, each one true the moment it is read (Kevin, 2026-10-03). The line
    promises a fix on a page, so it names the paid HubSpot plan that fix
    needs, as every such claim in this product does.
  */
  startNote: 'A free account. Your first page fixed free, with no card. It needs a paid HubSpot plan.',
}


/*
  The section under the hero: what the software does, in order.

  It used to close on three more lines: the "not a guarantee" disclaimer,
  the HubSpot tier requirement, and the Clean Pro trust line. Kevin removed
  all three from the home page on 2026-09-15. The tier requirement still
  appears on /pricing and /products/deploy, which is where somebody about to
  buy reads it; the methodology page still refuses the ranking promise in
  writing.
*/
export const factsSection = {
  headline: 'From business facts to live implementation.',
  body:
    'SuperSchema reads your pages, writes structured data and AI-readable files from what they say, and publishes them to your HubSpot CMS site. A live-page check confirms it landed.',
  linkText: 'See pricing plans',
  linkUrl: '/pricing',
  /*
    The demonstration beside the words: the four things that land, in the
    order they land, acted out rather than listed. No counts, no site name.
  */
  lands: {
    label: 'One page, start to finish',
    rows: ['Page read', 'Structured data written', 'Published to HubSpot CMS', 'Live page checked'],
    note: 'Confirmed on the page itself, not in a dashboard.',
  },
}

// Hero stats
export const heroStats: Stat[] = [
  { value: '24/7', label: 'AI Monitoring' },
  { value: '99.9%', label: 'Accuracy Rate' },
  { value: '<30s', label: 'Average Scan Time' },
]

export const statsSection = {
  headline: 'Your customers already use AI every day.',
  summary:
    'The customers worth winning are already in AI. The question is whether AI knows about you or about your competitor.',
  sourceNote: 'G2 Answer Economy Report (n=1,076). HubSpot Spring 2026 Spotlight (n=850). April 2026.',
}

export const evidenceStats: EvidenceStat[] = [
  {
    value: '51%',
    headline: 'of B2B customers start in AI',
    description: 'Software customers now begin vendor research in AI chatbots, not Google.',
    source: 'G2 Answer Economy Report (n=1,076)',
  },
  {
    value: '69%',
    headline: 'switched vendors because of AI',
    description:
      'B2B customers chose a different vendor than they planned, with 1 in 3 choosing a brand they had never heard of.',
    source: 'G2 Answer Economy Report',
  },
  {
    value: '4.4x',
    headline: 'higher conversion from AI traffic',
    description: 'Visitors arriving from AI convert 4.4 times better than organic search traffic.',
    source: 'HubSpot Spring 2026 (n=850)',
  },
]

// Problem section
export const problemSection = {
  headline: 'Humans can understand your website. AI? Not so much.',
  intro:
    'Most websites were built for people, search engines, and conversion funnels. AI is a little extra needy... Structured, readable, up-to-date context about your business is what it craves.',
  closer:
    'When that context is missing, AI may misrepresent you, recommend someone else, or worse... miss you entirely. SuperSchema gives AI the context it needs to understand you.',
}

export const problems: Problem[] = [
  {
    title: 'AI describes the wrong business',
    description: 'When your pages do not give AI a clear source of truth, it can misunderstand what your business actually does.',
  },
  {
    title: "AI invents services you don't offer",
    description:
      'Weak or missing context gives AI room to fill in blanks with services, locations, pricing, or proof that are not accurate.',
  },
  {
    title: 'AI uses outdated pricing',
    description:
      'Quiet page changes can leave stale schema and AI-readable files live unless the AI-facing layer is monitored and resynced.',
  },
  {
    title: 'AI can miss you entirely',
    description:
      'If AI cannot parse your key facts, it may recommend a competitor with cleaner signals instead.',
  },
]

// Solution section
export const solutionSection = {
  headline: 'SuperSchema: See your AI visibility clearly',
  intro:
    "We help you understand how AI systems read your business, generate the schema markup and AI-readable files you need, and track which bots are actually crawling your site. No guesswork. No jargon. Just clarity.",
  positioningStatement:
    'Other tools generate schema. SuperSchema shows you the full picture.',
}

export const solutions: Solution[] = [
  {
    title: 'Insights before action',
    headline: 'Know Before You Act',
    description:
      "Our site scan reveals exactly what AI sees (and doesn't see) about your business. No guessing, no assumptions.",
  },
  {
    title: 'Identity, not just markup',
    headline: 'Build a Connected Identity',
    description:
      "We don't just generate schema tags. We help you create a complete, connected picture of your business that AI can trust.",
  },
  {
    title: 'Visibility you can actually see',
    headline: 'Track What Actually Matters',
    description:
      'Watch AI bots visit your site in real-time. Know exactly which engines are paying attention.',
  },
]

// Positioning section
export const positioningSection = {
  badge: 'What SuperSchema is',
  headline: 'The AI-facing layer of your business',
  description:
    "SuperSchema helps businesses stay visible, understood, and trusted by AI systems. It gives AI a clear, consistent understanding of your business: who you are, what you do, and where you operate, even as your website, content, and tools change.",
  keyMessage:
    "SuperSchema keeps AI's understanding of your business footprint up to date as technology and AI evolve, so you can focus on running your business.",
  tagline: {
    part1: 'SEO gets you found.',
    part2: 'We get you understood.',
  },
  aiSystems: ['ChatGPT', 'Perplexity', 'Google AI', 'Claude'],
}

// Features section
export const featuresSection = {
  headline: "What you'll actually see inside",
  subheadline: 'Real signals. Real clarity. No guessing.',
}

export const features: Feature[] = [
  {
    iconName: 'Search',
    title: 'AI Visibility Baseline',
    description: 'A clear snapshot of how well AI understands your business today.',
  },
  {
    iconName: 'Bot',
    title: 'AI Activity & Attention',
    description: "See which AI systems are visiting your site, and which aren't.",
  },
  {
    iconName: 'BarChart3',
    title: 'Clarity & Coverage Summary',
    description: "What AI understands clearly, and what it's missing.",
  },
  {
    iconName: 'Zap',
    title: 'AI-Powered Schema Generation',
    description: 'Our AI analyzes your content and generates the right schema types automatically.',
  },
  {
    iconName: 'FileText',
    title: 'Alternate Content Files',
    description: 'Generate AI-friendly .md and .txt versions of your content.',
  },
  {
    iconName: 'HubSpot',
    title: 'HubSpot Integration',
    description: 'Connect your HubSpot account and deploy schema with one click.',
  },
  {
    iconName: 'Globe',
    title: 'Site Discovery & URL Mapping',
    description: "Drop in your domain and we'll crawl and map every URL.",
  },
  {
    iconName: 'Sparkles',
    title: 'One-Click Optimization',
    description: 'Our AI applies best practices automatically, click once, improve instantly.',
  },
  {
    iconName: 'Users',
    title: 'Team Accounts',
    description: 'Collaborate with your whole team on AI visibility strategy.',
  },
]

export const whyYouWinSection = {
  headline: 'Pages people love, that AI can also read.',
  summary:
    'Most tools hand you a to-do list. SuperSchema does the work, then checks every night that it is still done.',
}

export const advantages: Advantage[] = [
  {
    headline: 'Written from your own page',
    description:
      'We generate the markup and the AI-readable files from what your site already says, and never add what it does not.',
  },
  {
    headline: 'Nothing quietly breaks',
    description:
      'A page gets rebuilt, a plugin updates, and what AI was told about you disappears. We check every night and put it back.',
  },
  {
    headline: 'See who has been reading your site',
    description:
      'Which AI agents visited, how often, and which of your pages they spent their time on.',
  },
  {
    headline: 'Know where you stand',
    description:
      'One score for how much of your business AI can understand, and the clearest next thing to do about it.',
  },
]

/*
  Real reviews, carried whole. The head is the reviewer's own title, including
  the parts asking for things we have not built. No rating is stored or shown:
  this product has published invented ones before.
*/
/*
  Where the reviews come from.

  All five are from the SuperSchema listing on the HubSpot marketplace, which
  is HubSpot's own property, and saying so is worth more than the band's old
  generic title. Nothing about the reviews themselves changes: a review here
  is words and a name, headlines are kept whole including the half asking us
  for something we have not built, and no review carries a rating.
*/
export const reviewsSection = {
  title: 'What people say on the HubSpot marketplace.',
}

export const testimonials: Testimonial[] = [
  {
    head: 'Works Great For Syncing',
    said: "It's a great tool for syncing schema and it saves time instead of manually creating the schema. I like how it gives a grade of the whole site highlighting which pages need Schema or need to be improved. As well as a percentage to highlight the progress made to demonstrate to clients.",
    who: 'Garcia, J.',
  },
  {
    head: 'Excellent tool that saves hours of manual work, just needs domain hosting for markdown files',
    said: 'SuperSchema is a great product. Very well done and long overdue to have a tool to create schema for HubSpot hosted websites. I have been creating it manually for years and more recently with the help of AI.',
    who: 'Orecchio, D.',
  },
  {
    /*
      A non-breaking space between the last two words, and nothing else.

      `text-wrap: pretty` could not pull this one back, so the headline
      ended with "recommendable!" alone on its own line. The reviewer's own
      title is kept whole by rule, and it still is: not a character of what
      Eimer wrote has changed, and a reader sees the identical sentence. The
      only thing altered is where the line is allowed to break.
    */
    head: 'Perfect HubSpot integration and, as always, fantastic support - absolutely recommendable!',
    said: 'Good operation, integration with HubSpot works very well.',
    who: 'Eimer, C.',
  },
  {
    head: "Excellent tool with incredibly helpful and responsive developer - honestly don't want to share this advantage!",
    said: "SuperSchema is excellent and Kevin (the brains behind this awesome app) is incredibly helpful and responsive. I don't really want to leave a review as I want to keep the SuperSchema advantage all to myself!",
    who: 'Wilkinson, M.',
  },
  {
    head: 'Amazing results with impressions jumping from thousands to tens of thousands in just two weeks, though needs agency login and landing page integration',
    said: 'Really easy tool to use, love the push to HubSpot feature for the code. Would like to have more than 2 reviews of the schema for more AI impact, although this is a good number to start with. Great support from Kevin if you need it- as always!',
    who: 'Kerr, D.',
  },
]

// Built For section
export const builtForSection = {
  headline: 'Engineered for real businesses',
  subheadline:
    "SuperSchema is built for companies that take their online presence seriously. If that's you, you're in the right place.",
}

export const builtFor: BuiltForItem[] = [
  {
    iconName: 'Building2',
    title: 'Sites running on HubSpot',
    description:
      'Your pages, blog posts and landing pages already live in one place. We write the schema for them and deploy it there in one click.',
  },
  {
    iconName: 'TrendingUp',
    title: 'Marketing Teams',
    description: 'Teams protecting an SEO moat who understand that AI search is the next frontier.',
  },
  {
    iconName: 'Users',
    title: 'Agencies & Consultants',
    description: 'Partners who deliver measurable results and need enterprise-grade tooling.',
  },
  {
    iconName: 'Briefcase',
    title: 'Mid-Market & Enterprise',
    description: 'Organizations that need governance and reliability, not just another tool.',
  },
]

export const notFor: string[] = [
  'AI-wrapper startups building on hype',
  'Hobby projects and side hustles',
  'Churn-and-burn affiliate sites',
  'Anyone looking for free-tier support',
]

export const notForDisclaimer =
  "We'd rather be upfront than waste your time. SuperSchema probably isn't for you if you're building:"

export const notForCloser = "No judgment. We've just learned who we serve best."

// Pricing section
export const pricingSection = {
  headline: 'Simple, transparent pricing',
  subheadline: 'Run a free scan. Scale with monitoring as you grow.',
  enterpriseText: 'Agency & implementation options available',
  enterprisePriceRange: '$5k to $20k+',
  enterpriseCtaText: 'Book Consult',
}

export const pricingTiers: PricingTier[] = [
  {
    title: 'Credit Packs',
    subtitle: 'Perfect for getting started',
    price: 'From $19.99',
    priceNote: 'Pay as you go',
    features: [
      '1 credit = 1 URL with full schema',
      'Credits never expire',
      'All schema types included',
      '2 AI refinements per schema',
      'Limited site scanning',
    ],
    ctaText: 'Start Free Trial',
    ctaUrl: '/sign-up',
    isPopular: false,
  },
  {
    title: 'Founding Access (Beta)',
    subtitle: 'Full platform, no sales call',
    price: '$25',
    pricePeriod: '/month',
    priceNote: '$50/mo',
    features: [
      '25 credits included monthly',
      'Unlimited bot tracking',
      'Full site scoring & reports',
      'Team collaboration',
      'Priority support',
    ],
    ctaText: 'Start Free Trial',
    ctaUrl: '/sign-up',
    isPopular: true,
    popularText: 'Save 50% · limited time',
  },
]

export const commercialPathSection = {
  headline: 'Scan first. Audit the gaps. Monitor what matters.',
  lede:
    'AI does not just need to read your site. It needs to understand what you sell, who it is for, and what somebody should do next.',
}

export const commercialPathSteps: CommercialPathStep[] = [
  {
    step: '1',
    title: 'Scan',
    description: 'A fast read on whether AI can understand the key facts on your website.',
  },
  {
    step: '2',
    title: 'Audit',
    description: 'The gaps in priority order, each one cited to the page it came from.',
  },
  {
    step: '3',
    title: 'Monitor',
    description:
      'We watch for page changes and put back what a rebuild took away. Growth plans and above can also publish a SuperSchema Registry listing.',
  },
]

// FAQs
export const faqs: FAQ[] = [
  {
    question: 'What is AI visibility and why does it matter?',
    answer:
      "AI visibility refers to how well AI search engines (like ChatGPT, Perplexity, Google AI Overviews) can find, understand, and accurately represent your business. As more people use AI for answers instead of traditional search, being visible to these systems is becoming critical for getting discovered. Schema markup is the language AI uses to understand your content. Without it, you're essentially invisible.",
  },
  {
    question: 'How is SuperSchema different from regular SEO tools?',
    answer:
      "Traditional SEO tools focus on keywords and backlinks for Google search rankings. SuperSchema focuses on schema markup, AI-readable files, and AI readiness, helping AI systems accurately understand and cite your business. We show you what AI sees, generate the markup it needs, and track which AI bots are actually visiting your site. It's SEO evolved for the AI era.",
  },
  {
    question: 'I already have schema markup. Do I need SuperSchema?',
    answer:
      "Maybe! Our site scan will show you exactly how complete and accurate your existing schema is. Many sites have schema but it's incomplete, outdated, or has conflicting information. We'll identify gaps and help you fix them. Plus, our bot tracking shows you whether AI engines are actually visiting. Most people have no idea.",
  },
  {
    question: 'How does the site scan work?',
    answer:
      "Drop in your URL and we'll crawl your pages, analyze your existing schema markup, check for conflicts and missing data, and give you a detailed AI-readiness score. You'll see exactly what AI can (and can't) understand about your business, along with specific recommendations for improvement.",
  },
  {
    question: 'What AI bots can you track?',
    answer:
      "We track all major AI crawlers including GPTBot (OpenAI/ChatGPT), ClaudeBot (Anthropic), PerplexityBot, Google-Extended, and more. You'll see visit frequency, which pages they're accessing, and trends over time. It's the visibility into your AI visibility that you've never had before.",
  },
  {
    question: 'How quickly will I see results?',
    answer:
      "Schema implementation is instant. Your generated markup is ready to use immediately. AI engines typically re-crawl sites within days to weeks. Our bot tracking will show you when they visit so you're not guessing. SEO improvements from better schema can show up in Google rich results within days, while AI search improvements vary by platform.",
  },
  {
    question: 'What if I need help implementing?',
    answer:
      "For most users, our copy-paste JSON-LD or HubSpot integration makes implementation simple. For larger sites or complex needs, our Enterprise plan includes full implementation support. We'll handle everything from audit to deployment. Just reach out to our sales team.",
  },
  {
    question: 'Is there a free trial?',
    answer:
      'Yes. A free account shows you one of your HubSpot pages as AI sees it and, on a paid HubSpot plan, fixes your first page free, with no card.',
  },
]

// CTA section
export const ctaSection = {
  headline: 'SuperSchema makes sure AI gets your business right.',
  subheadline: 'We set it up, check it every night, and put it back when your site changes.',
  ctaText: 'Start free',
  ctaUrl: '/sign-up',
  /* Read only by the robot twin's closing band; the page shows heroContent.startNote under its second Start free button, so the twin says the same. */
  footnote: heroContent.startNote,
}

// About/Summary content for AI markdown generation
export const aboutContent = {
  intro:
    'SuperSchema is an AI clarity system that helps businesses compare what their website says with what AI can understand, then generate and monitor the structured files AI needs.',
  problem:
    'Most businesses look clear to humans but confusing to AI. Information is scattered, key facts are hard to parse, and page changes can leave stale signals live.',
  solution:
    'SuperSchema solves this with a site scan, automated schema and AI-readable file generation, monitoring, bot tracking, and clear next steps.',
  differentiator:
    "Unlike tools that only generate schema tags, SuperSchema gives you the AI-facing layer of your business and keeps that layer aligned as your website changes.",
}
